import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { useIntl } from 'react-intl';
import useSearch from 'src/registry/hooks/useSearch';
import { createColumnHelper } from '@tanstack/react-table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { booleanBadgeOptions } from 'src/utils/constants';
import useFilters from 'src/registry/hooks/useFilters';
import { subscriptionsMessages } from 'src/features/subscriptions/utils/messages';
import { useListSubscriptionItems } from 'src/features/subscriptionItems/api/useListSubscriptionItems';
import { useParams } from 'react-router-dom';
import { SubscriptionItem } from 'src/features/subscriptionItems/utils/types';
import { subscriptionItemsMessages } from 'src/features/subscriptionItems/utils/messages';
import { commonMessages } from 'src/utils/messages';
import { servicePlanItemsMessages } from 'src/features/servicePlanItems/utils/messages';
import { useBillableMetricMap } from 'src/features/billableMetrics/hooks/useBillableMetricMap';
import CreateSubscriptionItemFormModal from 'src/features/subscriptionItems/components/forms/CreateSubscriptionItemFormModal';
import CellSubscriptionItemActions from 'src/features/subscriptionItems/components/tables/CellSubscriptionItemActions';
import useSorting from 'src/registry/hooks/useSorting';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';

const SubscriptionItemsTable = () => {
    const { formatMessage } = useIntl();
    const { subscriptionId = '' } = useParams();

    const permissionManageSubscriptions = useHasPermissions([PERMISSION.MANAGE_SUBSCRIPTIONS]);

    const metricMap = useBillableMetricMap();

    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination({});
    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'start_date', desc: false }],
    });

    const {
        data: subscriptionItemsData,
        isLoading,
        isError,
    } = useListSubscriptionItems({
        subscriptionId,
        ...querySearchOptions,
        ...queryFiltersOptions,
        ...querySortingOptions,
        ...queryPaginationOptions,
        config: { enabled: !!subscriptionId },
    });

    const columnHelper = createColumnHelper<SubscriptionItem>();

    const columns = [
        columnHelper.accessor('service_plan_item.name', {
            header: DataTableColumnHeader,
            cell: (ctx) => (
                <CellLinkWithIcon
                    {...ctx}
                    to={`/subscriptions/${subscriptionId}/subscription-item/${ctx.row.original.id}`}
                    render={(name, row) => (
                        <div>
                            <div className="text-sm font-medium whitespace-nowrap">{name}</div>
                            <div className="text-sm text-gray-600">{row.service_plan_item.code}</div>
                        </div>
                    )}
                />
            ),
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('service_plan_item.billable_metric', {
            header: DataTableColumnHeader,
            // `billable_metric` is a bare UUID on the wire - the name has to come from the lookup.
            cell: ({ row }) => {
                const metric = metricMap.get(row.original.service_plan_item.billable_metric ?? '');
                if (!metric) return '-';
                return (
                    <div>
                        <div className="text-sm font-medium">{metric.name}</div>
                        <div className="text-sm text-gray-600">{metric.code}</div>
                    </div>
                );
            },
            meta: {
                title: formatMessage(servicePlanItemsMessages.metric),
            },
        }),
        columnHelper.accessor('external_item_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(subscriptionsMessages.externalId),
            },
        }),
        columnHelper.accessor('is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            cell: (ctx) => {
                return ctx.row.original.is_active !== null ? <CellBadge {...ctx} /> : '-';
            },
            meta: {
                title: formatMessage(subscriptionItemsMessages.isActive),
                badgeOptions: booleanBadgeOptions,
            },
        }),
        columnHelper.accessor('start_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.startDate),
            },
        }),
        columnHelper.accessor('end_date', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.endDate),
            },
        }),
        columnHelper.accessor('stopped_on', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(subscriptionsMessages.stoppedOn),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellSubscriptionItemActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable<SubscriptionItem, unknown>
            columns={columns}
            data={subscriptionItemsData}
            semantics={{
                title: formatMessage(subscriptionItemsMessages.subscriptionItems),
                entryName: formatMessage(subscriptionItemsMessages.item).toLowerCase(),
                entryNames: formatMessage(subscriptionItemsMessages.items).toLowerCase(),
                placeholder: formatMessage(subscriptionItemsMessages.item).toLowerCase(),
            }}
            search={search}
            sorting={sorting}
            filters={filters}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(permissionManageSubscriptions && { newEntry: CreateSubscriptionItemFormModal }),
            }}
        />
    );
};

export default SubscriptionItemsTable;
